import { postReq } from 'src/services/http';
import { format } from 'svelte-i18n';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { get } from 'svelte/store';
import type { Device } from 'src/types/Device';
import type { CloudApiResponse } from 'src/types/responses/CloudApiResponse';
import { _deepClone } from '../utilities';
import { isXMODDevice, real_id } from '../interface_helpers';
import { mapXMODComponentToCategoryAndMode } from '../xmod_helpers/xmod_common_helpers';
import { toast } from '@Shelly/shelly-design-system/index';

const _format = get(format);

const devicesInProgress = {
	created: {},
	deleted: {},
};

/**
 * Updates channel devices based on status.sys.ch components for XMOD devices
 *
 * When an XMOD device has multiple components in status.sys.ch (e.g., ['rgbw:0', 'cct:0']),
 * we automatically split it into separate channel devices:
 * - deviceId (main device) → uses first component (index 0)
 * - deviceId_1 → second component (index 1)
 * - deviceId_2 → third component (index 2), etc.
 *
 * @param {string} realID - The real device ID
 * @param {Device} realDevice - The main device object
 * @param {object} status - The device status containing sys.ch component array
 * @param {object} settings - The device settings
 * @param {string[]} xmodChannels - Array of existing XMOD channel IDs
 * @returns {Promise<void>}
 */
const updateChannelDevicesFromComponents = async (
	realID: string,
	realDevice: Device,
	status: any,
	settings: any,
	xmodChannels: string[],
) => {
	if (!status?.sys?.ch) {
		return;
	}

	if (Array.isArray(status.sys.ch) && status.sys.ch.length > 1) {
		for (let i = 0; i < status.sys.ch.length; i++) {
			const component = status.sys.ch[i];
			const componentType = component.split(':')[0];
			const channelID = i === 0 ? realID : `${realID}_${i}`;

			const { category, mode } = mapXMODComponentToCategoryAndMode(componentType as any);

			if (i === 0) {
				if (realDevice.category !== category || realDevice.mode !== mode) {
					const updatedDevice = {
						...realDevice,
						category,
						mode,
					};
					try {
						const res = await postReq(`/interface/device/save`, {
							id: realID,
							data: JSON.stringify(updatedDevice),
						});

						if (res?.isok) {
							devices.update(updatedDevice);
						} else {
							throw new Error('Failed to update main device');
						}
					} catch (error) {
						console.error(error);
					}
				}

				continue;
			}

			if (get(devices)[channelID]) {
				continue;
			}

			if (devicesInProgress.created[channelID]) {
				continue;
			}
			devicesInProgress.created[channelID] = true;

			const cloneDevice: any = _deepClone(realDevice);
			const position = realDevice.position + 1;
			delete cloneDevice.mainActionType;
			delete cloneDevice.statusLabelParam;
			delete cloneDevice.virtualDecimalPrecision;

			const channelDevice: Device = {
				...cloneDevice,
				id: channelID,
				channel: i,
				gen: realDevice.gen,
				room_id: realDevice.room_id,
				type: realDevice.type,
				name: `${realDevice.name} ${i}`,
				category,
				mode,
				image: `images/device_images/${realDevice.type}.png`,
				localImage: realDevice.localImage,
				position,
			};

			try {
				const res = await postReq(`/interface/device/save`, {
					id: channelID,
					data: JSON.stringify(channelDevice),
				});

				if (res?.isok) {
					devices.update(channelDevice);
					toast.open({
						type: 'success',
						title: _format('success_global', {
							default: 'Success',
						}),
					});
				} else {
					toast.open({
						type: 'danger',
						title: _format('error_global', {
							default: 'Failed to update status',
						}),
					});
					throw new Error('Failed to update status');
				}
			} catch (error) {
				console.error('Error creating XMOD channel device:', error);
			}
			devicesInProgress.created[channelID] = false;
		}

		const expectedChannelCount = status.sys.ch.length;
		const existingChannelDevices = xmodChannels.filter((id) => {
			const match = id.match(/_(\d+)$/);
			return match && parseInt(match[1]) < 100;
		});

		for (const channelDeviceId of existingChannelDevices) {
			const match = channelDeviceId.match(/_(\d+)$/);
			if (match) {
				const channelIndex = parseInt(match[1]);
				if (channelIndex >= expectedChannelCount) {
					try {
						const res = await postReq(`/interface/device/bulk_delete`, {
							ids: JSON.stringify([channelDeviceId]),
						});

						if (res?.isok) {
							devices.remove(channelDeviceId);
						} else {
							throw new Error('Failed to delete channel device');
						}
					} catch (error) {
						console.error('Error deleting XMOD channel device:', error);
					}
				}
			}
		}
	} else if (Array.isArray(status.sys.ch) && status.sys.ch.length === 1) {
		const component = status.sys.ch[0];
		const componentType = component.split(':')[0];
		const { category, mode } = mapXMODComponentToCategoryAndMode(componentType as any);

		if (realDevice.category !== category || realDevice.mode !== mode) {
			const updatedDevice = {
				...realDevice,
				category,
				mode,
			};
			try {
				const res = await postReq(`/interface/device/save`, {
					id: realID,
					data: JSON.stringify(updatedDevice),
				});

				if (res?.isok) {
					devices.update(updatedDevice);
				} else {
					throw new Error('Failed to update main device');
				}
			} catch (error) {
				console.error('Error updating main XMOD device:', error);
			}
		}

		const numericChannelDevices = xmodChannels.filter((id) => {
			const match = id.match(/_(\d+)$/);
			if (match) {
				const channelIndex = parseInt(match[1]);
				return channelIndex < 100;
			}
			return false;
		});

		if (numericChannelDevices.length > 0) {
			try {
				const res = await postReq(`/interface/device/bulk_delete`, {
					ids: JSON.stringify(numericChannelDevices),
				});

				if (res?.isok) {
					numericChannelDevices.forEach((deviceId) => {
						devices.remove(deviceId);
					});
				} else {
					throw new Error('Failed to delete channel devices');
				}
			} catch (error) {
				console.error('Error deleting XMOD channel devices:', error);
			}
		}
	}
};

/**
 * Updates all channel devices for an XMOD device based on status and settings
 *
 * This is the main orchestrator function that:
 * - Creates/updates/deletes channel devices based on status.sys.ch components
 * - Updates the main device category/mode when component types change
 * - Synchronizes status and settings across all channel devices
 *
 * @param {string} id - The XMOD device ID
 * @param {object | null} settingsParam - Optional device settings (uses store if not provided)
 * @param {object | null} statusParam - Optional device status (uses store if not provided)
 * @returns {Promise<void>}
 *
 * @example
 * // Update channels after status change
 * await updateXMODChannels('XM123abc456VJTI123', null, newStatus);
 *
 * @example
 * // Update channels using current store values
 * await updateXMODChannels('XM123abc456VJTI123');
 */
export const updateXMODChannels = async (
	id: string,
	settingsParam: any = null,
	statusParam: any = null,
) => {
	const realID = real_id(id);
	const realDevice = get(devices)[realID];
	const status = statusParam || devices_status.getByRealId(realID);
	const settings = settingsParam || devices_settings.getByRealId(realID);

	if (!isXMODDevice(realDevice?.type)) {
		return;
	}

	const xmodChannels = Object.keys(get(devices)).filter((deviceId) => {
		return isXMODDevice(get(devices)?.[deviceId]?.type) && deviceId?.includes(realID + '_');
	});

	await updateChannelDevicesFromComponents(realID, realDevice, status, settings, xmodChannels);

	if (xmodChannels.length > 0) {
		devices.update(realDevice);
	}
};

/**
 * Removes all XMOD components (main device and channel devices)
 *
 * This function performs a bulk deletion of all devices associated with an XMOD ID,
 * including:
 * - The main device
 * - All channel devices (_1, _2, _3, etc.)
 *
 * All matching devices are deleted from the cloud and removed from the local store.
 *
 * @param {string} id - The XMOD device ID to remove all components for
 * @returns {Promise<CloudApiResponse | undefined>} Response from the bulk deletion API call, or undefined if no devices found
 *
 * @example
 * // Remove all components of an XMOD device
 * await removeXMODComponents('XM123abc456VJTI123');
 * // This will delete: XM123abc456VJTI123, XM123abc456VJTI123_1, XM123abc456VJTI123_2, etc.
 */
export const removeXMODComponents = async (id: string) => {
	const xmodChannels = Object.keys(get(devices)).filter((_id) => _id.includes(id));
	if (!xmodChannels.length) {
		return;
	}
	try {
		const res: CloudApiResponse = await postReq(`/interface/device/bulk_delete`, {
			ids: JSON.stringify(xmodChannels),
		});
		if (res?.isok) {
			xmodChannels.forEach((channelId) => {
				devices.remove(channelId);
			});
		} else {
			throw new Error('Failed to delete channel devices');
		}
		return res;
	} catch (error) {
		console.error(error);
	}
};
